import { useState, type CSSProperties, type ReactNode } from "react";
import {
  cardsPerPlayer,
  DECK_SIZES,
  deckSizeLabel,
  drainsCards,
  enabledWildKinds,
  halfUnitPicksAtRandom,
  hasHalfUnit,
  HALF_UNIT_WILDS,
  isDeckSize,
  isHalfDeck,
  MIN_CARDS_PER_PLAYER,
  minDeckSizeForPlayerCount,
  STARFALL_EVENT_KINDS,
  STARFALL_EVENT_NAMES,
  STREAK_MIN_PLAYERS,
  DROUGHT_MISSES_TO_MIST,
  BURNING_DECK_FIRST_BURN_AFTER,
  STREAK_TURNS_TO_IGNITE,
  mercyGap,
  supportsPlayerCount,
  totalCardCount,
  WILD_KINDS,
  type DeckConfig,
  type DeckSize,
  type HouseRules,
  type StarfallEventKind,
  type WildKind,
} from "@fiveout/core";
import { WildIcon } from "./WildIcon";

/** One line under each Constellations event's checkbox: what it does to the table. */
const EVENT_BLURBS: Record<StarfallEventKind, string> = {
  comets: "The next three cards of whoever filled the meter fly onto random piles.",
  storm: "Every card on the table is swept up and dealt back out.",
  bounty: "Two piles pay a free card for a correct guess, for a lap.",
  meteors: "Two piles lose their top card, and a third is blown apart.",
  swarm: "The next card of whoever filled the meter shatters and burns seven top cards.",
};

/** The wild toggles' accessible names and tooltips: the buttons themselves show the card's emblem, not a word. */
const WILD_LABELS: Record<WildKind, string> = { smash: "Smash", lightning: "Lightning", swap: "Swap", peek: "Peek" };

/** Everything this panel edits, in the shape every caller stores it. */
export interface TableConfig {
  deckConfig: DeckConfig;
  houseRules: HouseRules;
  turnTimerSeconds: number | null;
}

export interface TableConfigPanelProps extends TableConfig {
  playerCount: number;
  onChange: (next: TableConfig) => void;
  /**
   * The online lobby's switch for voice at this table (VoiceTableToggle). A
   * slot rather than a TableConfig field: it is not dealt - it rides its own
   * route and takes effect at once - and local play, which shares this panel,
   * has no voice to switch and no business loading the voice store.
   */
  voiceToggle?: ReactNode;
  /**
   * A table where every turn is timed whatever the host picks - online, where
   * the server auto-plays a player who walks away. There is no "off" to offer
   * there, so the checkbox goes and the slider runs over this range instead;
   * a null `turnTimerSeconds` is shown as the range's top, which is what the
   * server enforces without one.
   */
  requiredTimer?: { min: number; max: number; step: number };
}

/**
 * Deck size, wild toggles, house rules and turn timer — shared by Local Play
 * setup and the online lobby so the two can't drift apart.
 *
 * Deck size is fully independent of player count now, with one guard rail:
 * sizes that can't deal everyone at least MIN_CARDS_PER_PLAYER cards stay on the
 * slider's scale, greyed, and the knob stops short of them with the reason
 * shown, so the rule is learnable instead of mysterious.
 *
 * Everything below the deck is folded by default. Most hosts deal with every
 * wild on (the default, each time a table is set up) and the house rules as
 * they are, so the screen opens on the one decision every table makes.
 */
export function TableConfigPanel({ playerCount, deckConfig, houseRules, turnTimerSeconds, onChange, voiceToggle, requiredTimer }: TableConfigPanelProps) {
  const { size, wilds } = deckConfig;

  // A local table's timer runs 3-10s or is off; a required one is never off.
  const timerMin = requiredTimer?.min ?? 3;
  const timerMax = requiredTimer?.max ?? 10;
  const timerStep = requiredTimer?.step ?? 1;
  const timerSeconds = requiredTimer
    ? Math.min(timerMax, Math.max(timerMin, turnTimerSeconds ?? timerMax))
    : turnTimerSeconds;

  // Folded sections are the panel's choice and die with it, like Constellations.
  const [wildsOpen, setWildsOpen] = useState(false);
  const [houseRulesOpen, setHouseRulesOpen] = useState(false);
  // Constellations is the longest section and the one most hosts leave alone, so
  // it starts folded; the choice is the panel's and dies with it.
  const [starfallOpen, setStarfallOpen] = useState(false);

  // The knob stops at the smallest deck that seats everyone rather than landing
  // on a size the deal would refuse; the sizes below it stay on the scale, greyed.
  const smallestSize = minDeckSizeForPlayerCount(playerCount, wilds);
  function setSize(raw: number) {
    if (!isDeckSize(raw)) return;
    const nextSize = supportsPlayerCount({ size: raw, wilds }, playerCount) ? raw : smallestSize;
    if (nextSize !== size) onChange({ deckConfig: { size: nextSize, wilds }, houseRules, turnTimerSeconds });
  }

  function toggleWild(kind: WildKind) {
    onChange({ deckConfig: { size, wilds: { ...wilds, [kind]: !wilds[kind] } }, houseRules, turnTimerSeconds });
  }

  const wildsOn = enabledWildKinds(wilds);
  const wildsSummary =
    wildsOn.length === WILD_KINDS.length ? "All on" : wildsOn.length === 0 ? "None" : `${wildsOn.length} of ${WILD_KINDS.length} on`;
  // A half unit carries two wilds, so with more than two on, WHICH two is drawn
  // when the round is dealt. Said here because it is the one thing about the
  // toggles that is not what it looks like.
  const randomHalf = hasHalfUnit(size) && halfUnitPicksAtRandom(wilds);

  // Where the slider's scale stops being playable and where the knob sits, as
  // fractions of its travel; the track paints both.
  const lastStep = DECK_SIZES.length - 1;
  const sliderStyle = {
    "--slider-floor": DECK_SIZES.indexOf(smallestSize) / lastStep,
    "--slider-fill": Math.max(0, DECK_SIZES.indexOf(size)) / lastStep,
  } as CSSProperties;

  // Swap and Peek move and reveal cards but never remove one, so a table with
  // only those (or none) is warned the same way an all-off table always was.
  const noDrain = !drainsCards(wilds);
  const perPlayer = cardsPerPlayer(deckConfig, playerCount);
  // A streak is only ever spent on somebody else, so on a solo table there is
  // nothing for Blue Shell to change — see STREAK_MIN_PLAYERS.
  const streaksOffered = playerCount >= STREAK_MIN_PLAYERS;
  // Named by what differs from a default table, as `describeHouseRules` is.
  const houseRulesSummary =
    [houseRules.blueShell ? "Blue Shell" : null, houseRules.mercyRule ? null : "No Mercy", houseRules.droughtRule ? null : "No Drought", houseRules.burningDeck ? null : "No Burning Deck"]
      .filter(Boolean)
      .join(" · ") ||
    "Standard";
  // Constellations runs on any deck now. What is left to show is which events
  // are in the deck: a host switches them off one at a time, and the last one
  // standing cannot be - "no events" is the master switch's job, and a deck
  // with nothing to draw would be the rule on in name only.
  const starfallOff = houseRules.starfallOff ?? [];
  const eventsOn = STARFALL_EVENT_KINDS.filter((k) => !starfallOff.includes(k));
  function setEvent(kind: StarfallEventKind, on: boolean) {
    const off = on ? starfallOff.filter((k) => k !== kind) : [...starfallOff, kind];
    const ordered = STARFALL_EVENT_KINDS.filter((k) => off.includes(k));
    onChange({ deckConfig, houseRules: { ...houseRules, starfallOff: ordered }, turnTimerSeconds });
  }

  return (
    <div className="col">
      <div className="section-label">Deck</div>
      <div className="deck-slider" style={sliderStyle}>
        <div className="deck-slider-readout">{deckSizeLabel(size)}</div>
        <input
          type="range"
          aria-label="Deck size"
          aria-valuetext={deckSizeLabel(size)}
          min={DECK_SIZES[0]}
          max={DECK_SIZES[lastStep]}
          step={0.5}
          value={size}
          onChange={(e) => setSize(Number(e.target.value))}
        />
        <div className="deck-slider-scale" aria-hidden="true">
          {DECK_SIZES.map((d, i) => (
            <span
              key={d}
              className={`deck-slider-tick${d === size ? " is-current" : ""}${d < smallestSize ? " is-off" : ""}`}
              style={{ "--tick": i / lastStep } as CSSProperties}
            >
              {d}
            </span>
          ))}
        </div>
      </div>
      <div className="subtitle">
        {totalCardCount(deckConfig)} cards &middot; {perPlayer} each for {playerCount} player
        {playerCount === 1 ? "" : "s"}
      </div>
      {smallestSize > DECK_SIZES[0] && (
        <div className="subtitle subtle">
          Smaller decks are grayed out because every player needs at least {MIN_CARDS_PER_PLAYER} cards.
        </div>
      )}

      <button
        type="button"
        className={`section-label rules-toggle rules-fold${wildsOpen ? " is-open" : ""}`}
        aria-expanded={wildsOpen}
        aria-controls="table-config-wilds"
        onClick={() => setWildsOpen((o) => !o)}
      >
        Wild cards
        <span className="fold-summary">{wildsSummary}</span>
        <span className="rules-chevron" aria-hidden="true" />
      </button>
      {wildsOpen && (
        <div id="table-config-wilds" className="col">
          <div className="wild-toggles">
            {WILD_KINDS.map((kind) => (
              <button
                key={kind}
                className={`wild-toggle${wilds[kind] ? " primary" : ""}`}
                aria-pressed={wilds[kind]}
                aria-label={WILD_LABELS[kind]}
                title={WILD_LABELS[kind]}
                onClick={() => toggleWild(kind)}
              >
                <WildIcon kind={kind} />
              </button>
            ))}
          </div>
          {randomHalf && (
            <div className="subtitle subtle">
              {isHalfDeck(size)
                ? `A half deck holds ${HALF_UNIT_WILDS} wilds, picked at random from these each round.`
                : `Each full deck holds one of every wild that\u2019s on; the half deck adds ${HALF_UNIT_WILDS} more, picked at random each round.`}
            </div>
          )}
        </div>
      )}
      {/* Outside the fold: a host who switched off both drains should still see
          what that does to a round with the section closed. */}
      {noDrain && (
        <div className="subtitle warn">
          Nothing removes cards from play without Smash or Lightning, so rounds run much longer.
        </div>
      )}

      {/* Constellations (`starfall` in code) is its own section: a master switch
          and one box per event. The events stay visible, greyed, with the
          master off - the host's next move may be switching it back on, and a
          list that vanished would hide what that would bring. Their stored
          choices are untouched either way. */}
      <button
        type="button"
        className={`section-label rules-toggle rules-fold${starfallOpen ? " is-open" : ""}`}
        aria-expanded={starfallOpen}
        aria-controls="table-config-starfall"
        onClick={() => setStarfallOpen((o) => !o)}
      >
        Constellations
        <span className="rules-chevron" aria-hidden="true" />
      </button>
      {starfallOpen && (
        <div id="table-config-starfall" className="col">
        <label className="row option-row">
          <input
            type="checkbox"
            checked={houseRules.starfall}
            onChange={(e) =>
              onChange({ deckConfig, houseRules: { ...houseRules, starfall: e.target.checked }, turnTimerSeconds })
            }
          />
          <span className="col option-text">
            <span>Constellations</span>
            <span className="subtitle subtle">
              Three matching cards on top, or four in a row, light a star. Three stars fire one of the events below.
              {isHalfDeck(size) && " On a half deck only four in a row can: it holds two of each card."}
            </span>
          </span>
        </label>
        <div className="col option-sublist">
          {STARFALL_EVENT_KINDS.map((kind) => {
            const on = eventsOn.includes(kind);
            const last = on && eventsOn.length === 1;
            const disabled = !houseRules.starfall || last;
            return (
              <label
                key={kind}
                className={`row option-row${disabled ? " disabled" : ""}`}
                title={last && houseRules.starfall ? "One event has to stay on \u2014 switch Constellations off instead" : undefined}
              >
                <input type="checkbox" checked={on} disabled={disabled} onChange={(e) => setEvent(kind, e.target.checked)} />
                <span className="col option-text">
                  <span>{STARFALL_EVENT_NAMES[kind]}</span>
                  <span className="subtitle subtle">{EVENT_BLURBS[kind]}</span>
                </span>
              </label>
            );
          })}
        </div>
        </div>
      )}

      {/* Vinny's Rule and Jourdan's Streaks are part of the game, not options;
          Blue Shell, the Mercy Rule, Drought and Burning Deck are the house rules
          left to choose here. Blue Shell changes who a streak is aimed at, so it goes
          wherever Streaks do, and Mercy and Drought are catch-ups against other
          players - a solo run has none of them (STREAK_MIN_PLAYERS,
          MERCY_MIN_PLAYERS, DROUGHT_MIN_PLAYERS), so the
          section comes off entirely, heading and all, rather than showing boxes
          that could do nothing. */}
      {streaksOffered && (
        <>
          <button
            type="button"
            className={`section-label rules-toggle rules-fold${houseRulesOpen ? " is-open" : ""}`}
            aria-expanded={houseRulesOpen}
            aria-controls="table-config-house-rules"
            onClick={() => setHouseRulesOpen((o) => !o)}
          >
            House rules
            <span className="fold-summary">{houseRulesSummary}</span>
            <span className="rules-chevron" aria-hidden="true" />
          </button>
          {houseRulesOpen && (
          <div id="table-config-house-rules" className="col">
          <label className="row option-row">
            <input
              type="checkbox"
              checked={houseRules.mercyRule}
              onChange={(e) =>
                onChange({ deckConfig, houseRules: { ...houseRules, mercyRule: e.target.checked }, turnTimerSeconds })
              }
            />
            <span className="col option-text">
              <span>Mercy Rule</span>
              <span className="subtitle subtle">
                End a turn {mercyGap(perPlayer)} or more cards behind the average opponent (half of the {perPlayer}{" "}
                dealt) and your deck bursts: a third of the gap flies back onto the piles. Then it rests for three of
                your turns, and it stops once anyone is out.
              </span>
            </span>
          </label>
          <label className="row option-row">
            <input
              type="checkbox"
              checked={houseRules.droughtRule}
              onChange={(e) =>
                onChange({ deckConfig, houseRules: { ...houseRules, droughtRule: e.target.checked }, turnTimerSeconds })
              }
            />
            <span className="col option-text">
              <span>Drought</span>
              <span className="subtitle subtle">
                Take a pile {DROUGHT_MISSES_TO_MIST} turns in a row and your deck is wrapped in mist. The next pile you
                take, only the top half comes to you: the rest bursts out onto the other piles.
              </span>
            </span>
          </label>
          <label className="row option-row">
            <input
              type="checkbox"
              checked={houseRules.burningDeck}
              onChange={(e) =>
                onChange({ deckConfig, houseRules: { ...houseRules, burningDeck: e.target.checked }, turnTimerSeconds })
              }
            />
            <span className="col option-text">
              <span>Burning Deck</span>
              <span className="subtitle subtle">
                Go {STREAK_TURNS_TO_IGNITE + BURNING_DECK_FIRST_BURN_AFTER} turns in a row without taking a pile or
                spending your streak and the top card of your deck burns away as your next turn begins, then another
                every other turn. It never takes your last card.
              </span>
            </span>
          </label>
          <label className="row option-row">
            <input
              type="checkbox"
              checked={houseRules.blueShell}
              onChange={(e) =>
                onChange({ deckConfig, houseRules: { ...houseRules, blueShell: e.target.checked }, turnTimerSeconds })
              }
            />
            <span className="col option-text">
              <span>Blue Shell Mode</span>
              <span className="subtitle subtle">
                Nobody picks who a streak hits: the fire goes to whoever is closest to going out, or everyone
                tied for it. Out in front on your own, you can&rsquo;t use a streak at all.
              </span>
            </span>
          </label>
          </div>
          )}
        </>
      )}

      <div className="section-label">Pace</div>
      {!requiredTimer && (
        <label className="row">
          <input
            type="checkbox"
            checked={turnTimerSeconds !== null}
            onChange={(e) => onChange({ deckConfig, houseRules, turnTimerSeconds: e.target.checked ? 7 : null })}
          />
          Turn timer
        </label>
      )}
      {timerSeconds !== null && (
        <div className="slider-field">
          <input
            type="range"
            min={timerMin}
            max={timerMax}
            step={timerStep}
            value={timerSeconds}
            aria-label="Seconds per turn"
            style={{ "--slider-fill": (timerSeconds - timerMin) / (timerMax - timerMin) } as CSSProperties}
            onChange={(e) => onChange({ deckConfig, houseRules, turnTimerSeconds: Number(e.target.value) })}
          />
          <div className="subtitle">{timerSeconds} seconds per turn</div>
        </div>
      )}

      {voiceToggle}
    </div>
  );
}
